// DateRangeCalendar.tsx
import { useEffect, useRef, useState } from "react";
import { Button, CalendarCell, CalendarGrid, CalendarGridBody, CalendarGridHeader, CalendarHeaderCell, RangeCalendar } from "react-aria-components";
import { parseDate, CalendarDate } from "@internationalized/date";
import "./dateRangeCalendar.css";
import { I18nProvider, useDateFormatter } from "react-aria";
import { _, _any, Locale } from "i18n";
import { useOnClickOutside } from "usehooks-ts";
import { ArrowLeft, ArrowRight } from "ui-kit/src/icons";

/**
 * Extended version of DateRangeCalendar used by Splits Stats by Season
 */
interface BaseRangeCalendarExtendedProps {
    start: string;
    end: string;
    onChange: (start: string, end: string, isReset: boolean) => void;
    placeholder?: string;
    apiFormat?: boolean;
    iconArrow?: boolean;

    // NEW:
    expandInModal?: boolean;           // makes popup inline to extend height
    disableFuture?: boolean;           // prevents selecting future dates
    resetToken?: number;               // external reset signal (optional)
    onOpenChange?: (open: boolean) => void; // bubble open state up
}

interface DateRangeCalendarProps extends BaseRangeCalendarExtendedProps {
    locale: Locale;
    placeholder?: string;
}

function BaseRangeCalendarExtended({
                               start,
                               end,
                               onChange,
                               placeholder,
                               apiFormat = false,
                               iconArrow = false,

                               expandInModal = false,
                               disableFuture = false,
                               resetToken = 0,
                               onOpenChange,
                           }: BaseRangeCalendarExtendedProps) {
    const dateFormatter = useDateFormatter({ month: "long", year: "numeric" });

    const currentDate = new Date().toISOString().split("T")[0];
    const today = parseDate(currentDate);

    const containerRef = useRef<HTMLDivElement>(null);
    const calendarRef = useRef<HTMLDivElement>(null);

    const [show, setShow] = useState(false);
    const [range, setRange] = useState({
        start: start ? parseDate(start) : parseDate(currentDate),
        end: end ? parseDate(end) : parseDate(currentDate),
    });

    // Reset from parent
	useEffect(() => {
		if (start && end) {
			setRange({
				start: parseDate(start),
				end: parseDate(end),
			});
		} else {
			setRange({
				start: parseDate(currentDate),
				end: parseDate(currentDate),
			});
		}
	}, [resetToken, currentDate, start, end]);

    useEffect(() => {
        onOpenChange?.(show);
        // Autofocus the calendar when it opens
        if (show) {
            // react-aria RangeCalendar supports autoFocus; also focus container as fallback
            setTimeout(() => {
                // try focus any focusable inside
                const el = calendarRef.current?.querySelector<HTMLElement>("[tabindex], button, [role=grid]");
                el?.focus?.();
            }, 0);
        }
    }, [show, onOpenChange]);

    useOnClickOutside(containerRef, () => {
        setShow(false);
    });

    const formatDate = (date: CalendarDate) => `${date.day}/${date.month}/${date.year}`;
    const formatOutputDate = (date: CalendarDate) => (apiFormat ? date.toString() : formatDate(date));

    const handleApply = () => {
        onChange(formatOutputDate(range.start), formatOutputDate(range.end), false);
        setShow(false);
    };

    const handleCancel = () => {
        onChange("", "", false);
        setRange({
            start: parseDate(currentDate),
            end: parseDate(currentDate),
        });
        setShow(false);
    };

    const handleReset = () => {
        onChange("", "", true);
        setRange({
            start: parseDate(currentDate),
            end: parseDate(currentDate),
        });
    };

    return (
        <div ref={containerRef}>
            <div
                className="calendarInput-container"
                onClick={() => setShow(!show)}
            >
                {iconArrow && !(start && end) && <span className="icon-new icon-select-arrow-down" />}
                <input
                    readOnly
                    placeholder={placeholder}
                    value={start && end ? `${start} - ${end}` : ""}
                    className={`calendarInput ${start && end ? "hasValue" : ""}`}
                />

                {start && end && (
                    <span
                        className="icon-new icon-cross icon-white"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleReset();
                        }}
                    />
                )}
            </div>

            {show && (
                <div
                    className={`calendarContainer ${expandInModal ? "calendarContainerExpanded" : ""}`}
                    ref={calendarRef}
                >
                    <RangeCalendar
                        // @ts-ignore
                        value={range}
                        // @ts-ignore
                        onChange={setRange}
                        visibleDuration={{ months: 2 }}
                        pageBehavior="single"
                        autoFocus           // << grab focus when rendered
                        // Disable future dates
                        {...(disableFuture ? { maxValue: today } : {})}
                    >
                        {({ state: { visibleRange, timeZone } }) => (
                            <>
                                <header className="calendarHeading">
                                    <Button slot="previous">
                                        <ArrowLeft />
                                    </Button>
                                    {dateFormatter.formatRange(
                                        visibleRange.start.toDate(timeZone),
                                        visibleRange.end.toDate(timeZone)
                                    )}
                                    <Button slot="next">
                                        <ArrowRight />
                                    </Button>
                                </header>

                                <div style={{ display: "flex", gap: 30, overflow: "auto", flexWrap: "wrap", justifyContent: "center" }}>
                                    <CalendarGrid weekdayStyle="short">
                                        <CalendarGridHeader>
                                            {(day) => <CalendarHeaderCell style={{ color: "var(--blue)" }}>{_any(mapDay(day))}</CalendarHeaderCell>}
                                        </CalendarGridHeader>
                                        <CalendarGridBody>{(date) => <CalendarCell date={date} />}</CalendarGridBody>
                                    </CalendarGrid>

                                    <CalendarGrid weekdayStyle="short" offset={{ months: 1 }}>
                                        <CalendarGridHeader>
                                            {(day) => <CalendarHeaderCell style={{ color: "var(--blue)" }}>{_any(mapDay(day))}</CalendarHeaderCell>}
                                        </CalendarGridHeader>
                                        <CalendarGridBody>{(date) => <CalendarCell date={date} />}</CalendarGridBody>
                                    </CalendarGrid>
                                </div>
                            </>
                        )}
                    </RangeCalendar>

                    <div className="calendarBottom">
                        <span>{`${formatDate(range.start)} - ${formatDate(range.end)}`}</span>
                        <Button className="calendarButton-cancel" onPress={handleCancel}>
                            {_("btn.cancel")}
                        </Button>
                        <Button className="calendarButton-apply" onPress={handleApply}>
                            {_("daterange.applyLabel")}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

function mapDay(day: string) {
    const dayStrings: Record<string, string> = {
        Mon: "datepicker.days_short.monday",
        Tue: "datepicker.days_short.tuesday",
        Wed: "datepicker.days_short.wednesday",
        Thu: "datepicker.days_short.thursday",
        Fri: "datepicker.days_short.friday",
        Sat: "datepicker.days_short.saturday",
        Sun: "datepicker.days_short.sunday",
    };
    return dayStrings[day] || day;
}

export default function DateRangeCalendarExtended(props: DateRangeCalendarProps) {
    const { placeholder, locale, ...rest } = props;
    return (
        <I18nProvider locale={locale}>
            <BaseRangeCalendarExtended placeholder={placeholder} {...rest} />
        </I18nProvider>
    );
}
